import React, { useState, useRef, useEffect, useCallback } from 'react';
import Typography from '../Typography';
import CustomModal from '../CustomModal';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

// Format date
const formatDate = (dateString) => {
    if (!dateString) return '[Insert Date]';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
};

// Format amount
const formatAmount = (amount) => {
    if (!amount) return '[Insert Amount]';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(3)}M`;
    if (amount >= 1000) {
        const thousands = amount / 1000;
        return thousands % 1 === 0 ? `$${thousands}K` : `$${(amount / 1000).toFixed(2)}K`;
    }
    return `$${amount.toLocaleString()}`;
};

const ContractSigning = ({
    contractSigned,
    setContractSigned,
    signature,
    user,
    investment,
    setSignature,
    error,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('draw');
    const sigCanvas = useRef();
    const [tempSignature, setTempSignature] = useState(null);
    const [contractTemplate, setContractTemplate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error1, setError] = useState(null);
    const [openCustomModal, setOpenCustomModal] = useState(false);
    const [customModalType, setCustomModalType] = useState('');
    const [customModalTitle, setCustomModalTitle] = useState('');
    const [customModalMessage, setCustomModalMessage] = useState('');
    const [customModalButtonText, setCustomModalButtonText] = useState('');

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const [templateResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/contract/contract-template`, {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 10000,
                }),
            ]);
            setContractTemplate(templateResponse.data.template);
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to load contract. Please try again or contact support.';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    });

    const handleOpenSignatureModal = () => {
        if (!contractTemplate) {
            setCustomModalType('error');
            setCustomModalTitle('Contract Not Available');
            setCustomModalMessage('Cannot add signature until the contract is loaded. Please try again or contact support.');
            setCustomModalButtonText('Okay');
            setOpenCustomModal(true);
            return;
        }
        setIsModalOpen(true);
    };

    useEffect(() => {
        fetchData();
    })

    const handleSignatureUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setTempSignature(reader.result); // Store temporary base64 for modal
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDrawSignature = () => {
        if (sigCanvas.current) {
            const canvas = sigCanvas.current.getCanvas();
            if (canvas) {
                const context = canvas.getContext('2d');
                // Create a trimmed canvas by checking non-transparent pixels
                const width = canvas.width;
                const height = canvas.height;
                const imageData = context.getImageData(0, 0, width, height);
                let minX = width, minY = height, maxX = 0, maxY = 0;

                for (let y = 0; y < height; y++) {
                    for (let x = 0; x < width; x++) {
                        const i = (y * width + x) * 4;
                        if (imageData.data[i + 3] > 0) { // Check alpha channel for non-transparent pixels
                            minX = Math.min(minX, x);
                            minY = Math.min(minY, y);
                            maxX = Math.max(maxX, x);
                            maxY = Math.max(maxY, y);
                        }
                    }
                }

                if (minX <= maxX && minY <= maxY) {
                    const trimWidth = maxX - minX + 1;
                    const trimHeight = maxY - minY + 1;
                    const trimmedCanvas = document.createElement('canvas');
                    trimmedCanvas.width = trimWidth;
                    trimmedCanvas.height = trimHeight;
                    const trimmedContext = trimmedCanvas.getContext('2d');
                    trimmedContext.drawImage(canvas, minX, minY, trimWidth, trimHeight, 0, 0, trimWidth, trimHeight);
                    setTempSignature(trimmedCanvas.toDataURL('image/png'));
                } else {
                    setTempSignature(null); // No signature drawn
                }
            }
        }
    };

    const handleRemoveSignature = () => {
        setSignature(null);
        setTempSignature(null);
    };

    const handleApplySignature = () => {
        if (tempSignature) {
            setSignature(tempSignature);
        }
        setContractSigned(true);
        setIsModalOpen(false);
    };

    const handleReply = () => {
        setTempSignature(null); // Clear temp signature on reply
        setIsModalOpen(false);
    };

    const renderContract = () => {
        if (!contractTemplate) return 'Loading contract...';

        // Define plans to match backend logic
        const plans = {
            'Plan 1': { term: 12, annualROI: 20, payoutSchedule: 'Quarterly' },
            'Plan 2': { term: 30, annualROI: 25, payoutSchedule: 'Semi-Annually' },
            'Plan 3': { term: 30, annualROI: 30, payoutSchedule: 'Lump Sum' },
        };

        // Get plan details or defaults
        const planDetails = plans[investment?.plan] || { term: 30, annualROI: 25, payoutSchedule: 'Semi-Annually' };

        // Calculate startDate and endDate if not provided
        const startDate = investment?.startDate || new Date();
        const endDate = investment?.endDate || new Date(new Date().setMonth(startDate.getMonth() + planDetails.term));

        // Calculate target amount (total ROI) if not provided
        const targetAmount = investment?.totalROI || (investment?.initialInvestment || 240000) * (planDetails.annualROI / 100);

        return contractTemplate
            .replace(/\[Insert Start Date\]/g, formatDate(startDate))
            .replace(/\[Insert End Date\]/g, formatDate(endDate))
            .replace(/\[Investor Full Legal Name\]/g, investment?.investorName || '[Name]')
            .replace(/\[Investor Address\]/g, user?.address || '[Address]') // No user.address available
            .replace(/\[Insert Amount\]/g, formatAmount(investment?.initialInvestment))
            .replace(/\[Insert Target Amount\]/g, formatAmount(targetAmount))
            .replace(/\[1-Year \| 2\.5-Year Semi-Annual \| 2\.5-Year Lump Sum\]/g, investment?.plan || '[Plan]')
            .replace(/\[Quarterly \| Semi-Annually \| Lump Sum\]/g, planDetails.payoutSchedule || 'Semi-Annually')
            .replace(/\[Status\]/g, investment?.status === 'wireConfirmed' ? 'Wire Transfer Pending (1-5 days)' : investment?.status || 'Pending');
    };

    return (
        <div className="md:px-28">
            {openCustomModal && (
                <CustomModal
                    type={customModalType}
                    title={customModalTitle}
                    message={customModalMessage}
                    onClose={() => setOpenCustomModal(false)}
                    onConfirm={() => setOpenCustomModal(false)}
                    colseText={customModalButtonText}
                    cancelButton={customModalType === 'confirm'}
                />
            )}

            <Typography
                text="CONTRACT"
                color="#FFF"
                size="24px"
                weight={400}
                lineheight="130%"
                transform="uppercase"
                className=""
            />
            <Typography
                text="Add an e-signature to your contract to complete the process."
                color="#FFF"
                opacity={0.6}
                size="14px"
                weight={400}
                className="mb-9 mt-3"
            />
            <div className="mt-8 border-[#262626] border-[1px]">
                <header className="h-[51px] px-6 flex justify-between items-center bg-[#333]">
                    <Typography
                        className=""
                        color="#FFF"
                        size="16px"
                        family="Supply"
                        weight={400}
                        lineheight="170%"
                        transform="uppercase"
                        text="contract details"
                    />
                </header>
                <div className="md:p-[30px] p-4 relative opacity-60">
                    <div
                        className="w-full whitespace-pre-wrap text-white text-left flex"
                        style={{ fontFamily: 'Supply', fontSize: '10px', fontStyle: 'normal', lineHeight: '15px', textTransform: 'uppercase' }}
                    >
                        {renderContract()}
                    </div>
                    {/* <textarea
                        value={renderContract()}
                        readOnly
                        disabled
                        rows={50}
                        style={{ fontFamily: 'Supply', fontSize: '10px', fontStyle: 'normal', lineHeight: '15px', textTransform: 'uppercase' }}
                        className="w-full bg-transparent  whitespace-pre-wrap text-left flex scrollbar-hide scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent overflow-y-auto overflow-x-auto p-5 text-white"
                    /> */}
                    {/* <Typography
                        size="10px"
                        weight={400}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="This Investment Agreement ('Agreement') is made and entered into as of [Date], by and between:"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={400}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text=" Ash Capital"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={400}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text={<>A private investment firm specializing in automotive asset-based lending and technology investments </>}
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={400}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text={<>('Company' or 'Ash Capital') </>}
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="AND"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="[Investor Full Legal Name]"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={400}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text={<>Located at: [Investor Address]</>}
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={400}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text={<>('Investor') </>}
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="1. Investment Details"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="The Investor agrees to invest capital into Ash Capital under the following terms:"
                    />
                    <ul className="text-[#FFF] px-5 list-disc">
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Investment Amount: $[Insert Amount] USD"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Plan Selected: [1-Year | 2.5-Year Semi-Annual | 2.5-Year Lump Sum]"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Target ROI: [20% | 90% | 100%]"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Investment Start Date: [Insert Date]"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Maturity Date: [Insert Date]"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Payout Schedule: [Quarterly | Semi-Annually | Lump Sum]"
                            />
                        </li>
                    </ul>
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="2. Documentation and Confirmation"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="Upon receipt of the full investment amount via wire transfer, Ash Capital shall:"
                    />
                    <ul className="text-[#FFF] px-5 list-disc">
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Confirm the receipt of funds with the Investor via email and/or phone;"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Activate the investment on the Investor’s dashboard;"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Provide real-time access to track ROI milestones and contract duration."
                            />
                        </li>
                    </ul>
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="A Promissory Note reflecting these details will be digitally or physically signed and stored securely in the investor’s dashboard for reference."
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="3. Use of Funds"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="Ash Capital will allocate funds at its discretion into diversified opportunities, including but not limited to:"
                    />
                    <ul className="text-[#FFF] px-5 list-disc">
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="UAE Real Estate Investments"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Automotive Asset-Based Lending"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Venture Incubator Projects"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Asset Arbitrage Models"
                            />
                        </li>
                        <li>
                            <Typography
                                className=""
                                size="10px"
                                weight={300}
                                color="#FFF"
                                lineheight="15px"
                                transform="uppercase"
                                family="Supply"
                                text="Cryptocurrency Portfolios"
                            />
                        </li>
                    </ul>
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="4. Payouts and Returns"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="Ash Capital commits to delivering returns based on the selected plan. Payouts will be made to the Investor via wire transfer or other agreed-upon method, according to the specified payout schedule."
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="5. Early Withdrawal & Termination"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="Early withdrawal is not permitted unless approved in writing by Ash Capital. In such cases, early termination fees or forfeiture of accrued ROI may apply."
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="6. Referral Program"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="Investor is eligible to participate in Ash Capital's referral program. Referral commissions are governed by a separate agreement and subject to verification."
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="7. Confidentiality"
                    />
                    <Typography
                        className=""
                        size="10px"
                        weight={300}
                        color="#FFF"
                        lineheight="15px"
                        transform="uppercase"
                        family="Supply"
                        text="Both parties agree to maintain strict confidentiality regarding all terms, communications, and data shared."
                    /> */}
                </div>
                <div className="mt-[42px] px-[95px] h-[1px]" style={{ background: 'linear-gradient(90deg, #000 0%, #DF0F11 49.5%, #000 100%)' }} />
                <div className="mt-7 mb-8">
                    <div className="flex md:px-10 px-4 w-full items-start">
                        <div className="flex flex-col items-center">
                            <div>
                                {signature ? (
                                    <div className="relative flex justify-between">
                                        <img
                                            src={signature}
                                            alt="Signature"
                                            className="max-h-[54.75px] md:mx-auto mb-2 md:max-w-[190px]"
                                        /* style={{ filter: 'invert(1)' }} */
                                        />
                                        <button
                                            onClick={handleRemoveSignature}
                                            className="absolute top-1/2 z-20 right-0 transform -translate-y-1/2"
                                            aria-label="Remove signature"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="24"
                                                height="25"
                                                viewBox="0 0 24 25"
                                                fill="none"
                                            >
                                                <g opacity="0.6">
                                                    <mask
                                                        id="mask0_579_623"
                                                        style={{ maskType: 'alpha' }}
                                                        maskUnits="userSpaceOnUse"
                                                        x="0"
                                                        y="0"
                                                        width="24"
                                                        height="25"
                                                    >
                                                        <rect y="0.375" width="24" height="24" fill="#D9D9D9" />
                                                    </mask>
                                                    <g mask="url(#mask0_579_623)">
                                                        <path
                                                            d="M8.10658 20.275C7.66291 20.275 7.28399 20.118 6.96983 19.804C6.65583 19.4899 6.49883 19.1109 6.49883 18.6673V6.97503H5.29883V5.67503H9.59883V4.59053H14.3988V5.67503H18.6988V6.97503H17.4988V18.6565C17.4988 19.1189 17.3434 19.5042 17.0326 19.8125C16.7217 20.1209 16.3412 20.275 15.8911 20.275H8.10658ZM16.1988 6.97503H7.79883V18.6673C7.79883 18.7571 7.82766 18.8309 7.88533 18.8885C7.94299 18.9462 8.01675 18.975 8.10658 18.975H15.8911C15.9681 18.975 16.0386 18.9429 16.1026 18.8788C16.1667 18.8148 16.1988 18.7443 16.1988 18.6673V6.97503ZM10.0028 17.175H11.3026V8.77503H10.0028V17.175ZM12.6951 17.175H13.9948V8.77503H12.6951V17.175Z"
                                                            fill="white"
                                                        />
                                                    </g>
                                                </g>
                                            </svg>
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="mt-[7.31px] md:block hidden"
                                width="191"
                                height="2"
                                viewBox="0 0 191 2"
                                fill="none"
                            >
                                <path opacity="0.4" d="M191 1.375H0" stroke="white" />
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" className="mt-[7.31px] md:hidden block" width="296" height="2" viewBox="0 0 296 2" fill="none">
                                <path opacity="0.4" d="M296 1.375H0" stroke="white" />
                            </svg>
                            {signature ? (
                                <Typography className="text-center" text="*Client Signature" color="#666" size="12px" />
                            ) : (
                                <button
                                    // onClick={() => setIsModalOpen(true)}
                                    onClick={handleOpenSignatureModal}
                                    style={{ padding: '10px 10px 10px 16px' }}
                                    className="text-white gap-[21px] inline-flex mt-5 h-8 items-center justify-center flex-shrink-0 rounded bg-[#DF0F11]"
                                >
                                    <Typography
                                        className=""
                                        size="12px"
                                        weight={500}
                                        color="#FFF"
                                        lineheight="16px"
                                        text="Add your signature"
                                    />{' '}
                                    <div>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="21"
                                            viewBox="0 0 20 21"
                                            fill="none"
                                        >
                                            <mask
                                                id="mask0_579_617"
                                                style={{ maskType: 'alpha' }}
                                                maskUnits="userSpaceOnUse"
                                                x="0"
                                                y="0"
                                                width="20"
                                                height="21"
                                            >
                                                <rect y="0.375" width="20" height="20" fill="#D9D9D9" />
                                            </mask>
                                            <g mask="url(#mask0_579_617)">
                                                <path
                                                    d="M9.375 13.5321V6.53208L7.32063 8.58646L6.44229 7.68271L10 4.125L13.5577 7.68271L12.6794 8.58646L10.625 6.53208V13.5321H9.375ZM5.25646 16.625C4.83549 16.625 4.47917 16.4792 4.1875 16.1875C3.89583 15.8958 3.75 15.5395 3.75 15.1185V12.859H5V15.1185C5 15.1827 5.02674 15.2415 5.08021 15.2948C5.13354 15.3483 5.19229 15.375 5.25646 15.375H14.7435C14.8077 15.375 14.8665 15.3483 14.9198 15.2948C14.9733 15.2415 15 15.1827 15 15.1185V12.859H16.25V15.1185C16.25 15.5395 16.1042 15.8958 15.8125 16.1875C15.5208 16.4792 15.1645 16.625 14.7435 16.625H5.25646Z"
                                                    fill="white"
                                                />
                                            </g>
                                        </svg>
                                    </div>
                                </button>
                            )}
                            <input
                                type="file"
                                name="signature"
                                id="signature"
                                accept="image/*"
                                onChange={handleSignatureUpload}
                                className="hidden"
                            />
                        </div>
                        {/* <div className="flex flex-col justify-center items-start">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="mt-[7.31px]"
                                width="191"
                                height="2"
                                viewBox="0 0 191 2"
                                fill="none"
                            >
                                <path opacity="0.4" d="M191 1.375H0" stroke="white" />
                            </svg>
                        </div> */}
                    </div>
                </div>
            </div>
            {error && (
                <Typography
                    text={error}
                    color="#EF4444"
                    size="14px"
                    weight={400}
                    className="mt-2"
                />
            )}

            {isModalOpen && (
                <div className="fixed inset-0 animate-backdropFadeIn bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-[#1B1B1B] p-6 rounded-lg w-[400px]">
                        <div className="flex border-b border-[#262626] mb-4">
                            <button
                                className={`px-4 py-2 ${activeTab === 'draw' ? 'border-b-2 border-red-700 text-white' : 'text-gray-400'
                                    }`}
                                onClick={() => setActiveTab('draw')}
                            >
                                <Typography text="Draw" color="#FFF" size="14px" weight={500} />
                            </button>
                            <button
                                className={`px-4 py-2 ${activeTab === 'upload' ? 'border-b-2 border-red-700 text-white' : 'text-gray-400'
                                    }`}
                                onClick={() => setActiveTab('upload')}
                            >
                                <Typography text="Upload" color="#FFF" size="14px" weight={500} />
                            </button>
                        </div>
                        {activeTab === 'upload' && (
                            <div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleSignatureUpload}
                                    className="mb-4 text-white"
                                />
                                {tempSignature && (
                                    <div className="relative">
                                        <img
                                            src={tempSignature}
                                            alt="Temp Signature"
                                            className="max-h-[100px] mb-2 mx-auto"
                                        /* style={{ filter: 'invert(1)' }} */
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        {activeTab === 'draw' && (
                            <div>
                                <SignatureCanvas
                                    ref={sigCanvas}
                                    penColor='#FFF'
                                    canvasProps={{ className: 'signature-canvas bg-black w-full h-40 mb-4' }}
                                    onEnd={handleDrawSignature}
                                />
                                {tempSignature && (
                                    <div className="relative text-center flex justify-center items-center">
                                        <img
                                            src={tempSignature}
                                            alt="Temp Signature"
                                            className="max-h-[100px] mx-auto mb-2"
                                        /* style={{ filter: 'invert(1)' }} */
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex justify-end gap-4 mt-4">
                            <button onClick={handleReply} className='bg-[#282828] text-white px-4 py-2'><Typography text="Reply" color="#FFF" size="14px" weight={500} /></button>
                            <button onClick={handleApplySignature} className='bg-[#DF0F11] text-white px-4 py-2'><Typography text="Apply" color="#FFF" size="14px" weight={500} /></button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractSigning;